import { initializeApp } from 'firebase/app'
import {
  createUserWithEmailAndPassword,
  FacebookAuthProvider,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  TwitterAuthProvider,
} from 'firebase/auth'
import { getDatabase } from 'firebase/database'
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore'

const config = {
  apiKey: process.env.GATSBY_API_KEY,
  authDomain: process.env.GATSBY_AUTH_DOMAIN,
  projectId: process.env.GATSBY_PROJECT_ID,
  storageBucket: process.env.GATSBY_STORAGE_BUCKET,
  messagingSenderId: process.env.GATSBY_MESSAGING_SENDER_ID,
  appId: process.env.GATSBY_APP_ID,
}

class Firebase {
  constructor() {
    const app = initializeApp(config)

    /* Firebase APIs */

    this.auth = getAuth(app)
    this.db = getDatabase(app)
    this.fs = getFirestore(app)

    /* Social Sign In Method Provider */

    this.googleProvider = new GoogleAuthProvider()
    this.facebookProvider = new FacebookAuthProvider()
    this.twitterProvider = new TwitterAuthProvider()
  }

  doCreateUserWithEmailAndPassword = (email, password) =>
    createUserWithEmailAndPassword(this.auth, email, password)

  doSignInWithEmailAndPassword = (email, password) =>
    signInWithEmailAndPassword(this.auth, email, password)

  doSignInWithGoogle = () => signInWithPopup(this.auth, this.googleProvider)

  doSignInWithFacebook = () => signInWithPopup(this.auth, this.facebookProvider)

  doSignInWithTwitter = () => signInWithPopup(this.auth, this.twitterProvider)

  doSendEmailVerification = () =>
    sendEmailVerification(this.auth.currentUser, {
      url: process.env.GATSBY_CONFIRMATION_EMAIL_REDIRECT,
    })

  doSignOut = () => this.auth.signOut()

  doPasswordReset = email => sendPasswordResetEmail(this.auth, email)

  createUser = async user => {
    const usersColRef = collection(this.fs, 'users')
    return addDoc(usersColRef, {
      ...user,
      isDeleted: false,
      creationTime: serverTimestamp(),
      creatorId: user.userId,
    })
  }

  createProfile = async profile => {
    const usersQuery = query(
      collection(this.fs, 'users'),
      where('userId', '==', profile.userId)
    )
    const usersSnapshot = await getDocs(usersQuery)
    usersSnapshot.forEach(document => {
      const docRef = doc(this.fs, 'users', document.id)
      setDoc(
        docRef,
        {
          ...profile,
          lastModificationTime: serverTimestamp(),
          lastModifierId: profile.userId,
        },
        { merge: true }
      )
    })
  }

  user = async userId => {
    const usersColRef = collection(this.fs, 'users')
    const usersQuery = query(usersColRef, where('userId', '==', userId))
    const querySnapshot = await getDocs(usersQuery)
    return querySnapshot.docs[0]
  }

  users = (snapshot, error) => {
    const usersColRef = collection(this.fs, 'users')
    const usersQuery = query(usersColRef, orderBy('creationTime'))
    return onSnapshot(usersQuery, snapshot, error)
  }

  createMessage = message => {
    const usersColRef = collection(this.fs, 'messages')
    return addDoc(usersColRef, {
      userId: message.userId,
      text: message.text,
      isDeleted: false,
      creationTime: serverTimestamp(),
      creatorId: message.creatorId,
    })
  }

  editMessage = message => {
    const messageRef = doc(this.fs, 'messages', message.uid)
    setDoc(
      messageRef,
      {
        text: message.text,
        lastModificationTime: serverTimestamp(),
        lastModifierId: message.lastModifierId,
      },
      { merge: true }
    )
  }

  removeMessage = messageId => {
    const messageRef = doc(this.fs, 'messages', messageId)
    setDoc(
      messageRef,
      {
        isDeleted: true,
      },
      { merge: true }
    )
  }

  message = messageId => {
    const userDocRef = doc(this.fs, 'messages', messageId)
    return getDoc(userDocRef)
  }

  messages = async count => {
    const messagesRef = collection(this.fs, 'messages')
    const messagesQuery = query(
      messagesRef,
      orderBy('creationTime'),
      limit(count)
    )
    const querySnapshot = await getDocs(messagesQuery)

    return querySnapshot.docs
  }

  onAuthUserListener = (next, fallback) =>
    onAuthStateChanged(this.auth, authUser => {
      if (authUser) {
        this.user(authUser.uid).then(dbUser => {
          if (dbUser && dbUser.exists()) {
            const user = {
              uid: authUser.uid,
              email: authUser.email,
              emailVerified: authUser.emailVerified,
              providerData: authUser.providerData,
              roles: {},
              ...dbUser.data(),
            }

            next(user)
          }
        })
      } else {
        fallback()
      }
    })
}

let firebase

function getFirebase() {
  if (!firebase) {
    firebase = new Firebase()
  }

  return firebase
}

export default getFirebase
